import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Plus, Pencil, Trash2, MailCheck, MailWarning, Send } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

type DepartmentRecipient = {
  id: number;
  department: string;
  email: string;
  name?: string | null;
  cc?: string | null;
  bcc?: string | null;
  isActive: boolean;
};

const DEPARTMENTS = [
  { key: "accounting", label: "Contabilidad" },
  { key: "operations", label: "Operaciones / PM" },
  { key: "management", label: "Gerencia" },
  { key: "info", label: "Info / General" },
];

interface RecipientForm {
  id?: number;
  department: string;
  email: string;
  name: string;
  cc: string;
  bcc: string;
  isActive: boolean;
}

const emptyForm = (department: string): RecipientForm => ({
  department,
  email: "",
  name: "",
  cc: "",
  bcc: "",
  isActive: true,
});

export default function EmailRecipients() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();

  const isAdmin = user?.role === "admin";

  const [activeTab, setActiveTab] = useState("accounting");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [form, setForm] = useState<RecipientForm>(emptyForm("accounting"));

  const { data: recipients, isLoading } = trpc.emailConfig.listRecipients.useQuery(undefined, {
    enabled: isAdmin,
  });
  const sendTestMutation = trpc.emailConfig.sendTestEmail.useMutation({
    onSuccess: (data) => {
      if (data.status === "dry_run") {
        toast.info("Dry-run: email simulado");
      } else if (data.status === "sent") {
        toast.success("Email de prueba enviado");
      } else {
        toast.error("Error al enviar email de prueba");
      }
    },
    onError: (err) => {
      toast.error("Error al enviar email de prueba: " + err.message);
    },
  });
  const deleteMutation = trpc.emailConfig.deleteRecipient.useMutation({
    onSuccess: () => {
      toast.success("Destinatario eliminado");
      setDeleteTarget(null);
      utils.emailConfig.listRecipients.invalidate();
    },
    onError: (err) => {
      toast.error("Error al eliminar: " + err.message);
    },
  });
  const upsertMutation = trpc.emailConfig.upsertRecipient.useMutation({
    onSuccess: () => {
      toast.success(form.id ? "Destinatario actualizado" : "Destinatario creado");
      setDialogOpen(false);
      utils.emailConfig.listRecipients.invalidate();
    },
    onError: (err) => {
      toast.error("Error al guardar: " + err.message);
    },
  });

  useEffect(() => {
    if (user && !isAdmin) {
      setLocation("/");
    }
  }, [user, isAdmin, setLocation]);

  if (!user || !isAdmin) {
    return null;
  }

  const recipientsByDept = DEPARTMENTS.map((dept) => ({
    ...dept,
    items: (recipients || []).filter((r: DepartmentRecipient) => r.department === dept.key),
  }));

  const handleEdit = (recipient: DepartmentRecipient) => {
    setForm({
      id: recipient.id,
      department: recipient.department,
      email: recipient.email,
      name: recipient.name || "",
      cc: recipient.cc || "",
      bcc: recipient.bcc || "",
      isActive: recipient.isActive,
    });
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setForm(emptyForm(activeTab));
    setDialogOpen(true);
  };

  const handleToggleActive = (recipient: DepartmentRecipient) => {
    upsertMutation.mutate({
      id: recipient.id,
      department: recipient.department,
      email: recipient.email,
      name: recipient.name ?? undefined,
      cc: recipient.cc ?? undefined,
      bcc: recipient.bcc ?? undefined,
      isActive: !recipient.isActive,
    });
  };

  const handleSendTest = (recipient: DepartmentRecipient) => {
    sendTestMutation.mutate({
      to: recipient.email,
      recipientId: recipient.id,
      department: recipient.department,
    });
  };

  const handleSave = () => {
    if (!form.email.trim()) {
      toast.error("El email es obligatorio");
      return;
    }
    upsertMutation.mutate({
      id: form.id,
      department: form.department,
      email: form.email.trim(),
      name: form.name.trim() || undefined,
      cc: form.cc.trim() || undefined,
      bcc: form.bcc.trim() || undefined,
      isActive: form.isActive,
    });
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate({ id: deleteTarget.id });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Destinatarios Email</h1>
            <p className="text-muted-foreground mt-2">
              Gestiona los destinatarios internos por departamento
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Destinatario
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            {DEPARTMENTS.map((dept) => (
              <TabsTrigger key={dept.key} value={dept.key}>
                {dept.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {recipientsByDept.map((dept) => (
            <TabsContent key={dept.key} value={dept.key} className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">{dept.label}</CardTitle>
                  <CardDescription>
                    {dept.items.length === 0
                      ? "No hay destinatarios configurados para este departamento"
                      : `${dept.items.length} destinatario${dept.items.length !== 1 ? "s" : ""}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : dept.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <MailWarning className="h-12 w-12 text-muted-foreground/40 mb-3" />
                      <p className="text-muted-foreground text-sm">
                        No hay destinatarios configurados para este departamento
                      </p>
                      <Button variant="outline" size="sm" className="mt-4" onClick={handleCreate}>
                        <Plus className="mr-2 h-3 w-3" />
                        Añadir destinatario
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {dept.items.map((recipient: DepartmentRecipient) => (
                        <div key={recipient.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium truncate">
                                {recipient.name || recipient.email}
                              </p>
                              {recipient.name && recipient.name !== recipient.email && (
                                <p className="text-sm text-muted-foreground truncate">
                                  {recipient.email}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {recipient.cc && (
                                <Badge variant="outline" className="text-[10px]">
                                  CC: {recipient.cc}
                                </Badge>
                              )}
                              {recipient.bcc && (
                                <Badge variant="outline" className="text-[10px]">
                                  BCC: {recipient.bcc}
                                </Badge>
                              )}
                              <Badge
                                variant={recipient.isActive ? "default" : "secondary"}
                                className="text-[10px]"
                              >
                                {recipient.isActive ? "Activo" : "Inactivo"}
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 ml-4 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendTest(recipient)}
                              disabled={sendTestMutation.isPending}
                              title="Enviar email de prueba"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleToggleActive(recipient)}
                              title={recipient.isActive ? "Desactivar" : "Activar"}
                            >
                              <MailCheck
                                className={`h-4 w-4 ${recipient.isActive ? "text-green-500" : "text-muted-foreground"}`}
                              />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEdit(recipient)}
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() =>
                                setDeleteTarget({
                                  id: recipient.id,
                                  name: recipient.name || recipient.email,
                                })
                              }
                              title="Eliminar"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{form.id ? "Editar Destinatario" : "Nuevo Destinatario"}</DialogTitle>
            <DialogDescription>
              {form.id
                ? "Modifica los datos del destinatario"
                : "Añade un nuevo destinatario de correo"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="dept">Departamento</Label>
              <Select
                value={form.department}
                onValueChange={(v) => setForm({ ...form, department: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((d) => (
                    <SelectItem key={d.key} value={d.key}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre del destinatario"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cc">CC</Label>
              <Input
                id="cc"
                value={form.cc}
                onChange={(e) => setForm({ ...form, cc: e.target.value })}
                placeholder="cc@ejemplo.com (opcional)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bcc">BCC</Label>
              <Input
                id="bcc"
                value={form.bcc}
                onChange={(e) => setForm({ ...form, bcc: e.target.value })}
                placeholder="bcc@ejemplo.com (opcional)"
              />
            </div>
            <div className="flex items-center space-x-2 pt-2">
              <Switch
                id="active-switch"
                checked={form.isActive}
                onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
              />
              <Label htmlFor="active-switch">Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={upsertMutation.isPending}>
              {upsertMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {form.id ? "Guardar Cambios" : "Crear Destinatario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar destinatario</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>¿Estás seguro de eliminar a <strong>{deleteTarget.name}</strong>? Esta acción no se puede deshacer.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
